import { createReducer, on } from '@ngrx/store';
import {
  ActivateKpiThread,
  ActivateThread,
  AskKpiQuestion,
  CreateSessionAndPostQuestion,
  CreateSessionAndPostQuestionFailed,
  CreateSessionAndPostQuestionSuccess,
  CreateSessionFailed,
  CreateSessionSuccess,
  LoadSessionHistory,
  LoadSessionHistoryFailed,
  LoadSessionHistorySuccess,
  LoadSessions,
  LoadSessionsFailed,
  LoadSessionsSuccess,
  LoadUsageSuccess,
  NewChat,
  OpenSession,
  PostQuestion,
  PostQuestionFailed,
  PostQuestionSuccess,
  RemoveConversation,
  TypeMessage,
} from '../actions/chatbot.actions';
import {
  Conversation,
  GENERAL_THREAD_ID,
  GENERAL_THREAD_TITLE,
  MAX_HYDRATED_CONVERSATIONS,
  PastSession,
  Sender,
  Usage,
  createConversation,
  kpiThreadId,
  mapHistoryToMessages,
  sessionThreadId,
} from '../models/chatbot.model';

export interface ChatbotState {
  conversations: Record<string, Conversation>;
  accessOrder: string[];
  pastSessions: PastSession[];
  loadingSessions: boolean;
  usage: Usage | null;
  activeThreadId: string;
  message: string;
}

const initialState: ChatbotState = {
  conversations: {
    [GENERAL_THREAD_ID]: createConversation(GENERAL_THREAD_ID, GENERAL_THREAD_TITLE),
  },
  accessOrder: [GENERAL_THREAD_ID],
  pastSessions: [],
  loadingSessions: false,
  usage: null,
  activeThreadId: GENERAL_THREAD_ID,
  message: '',
};

const ensureConversation = (state: ChatbotState, threadId: string): Conversation =>
  state.conversations[threadId] ?? createConversation(threadId, '');

const patchConversation = (
  state: ChatbotState,
  threadId: string,
  changes: Partial<Conversation>,
): Record<string, Conversation> => ({
  ...state.conversations,
  [threadId]: { ...ensureConversation(state, threadId), ...changes },
});

const touch = (accessOrder: string[], threadId: string): string[] => [
  ...accessOrder.filter((id) => id !== threadId),
  threadId,
];

const evict = (
  conversations: Record<string, Conversation>,
  accessOrder: string[],
  activeThreadId: string,
): { conversations: Record<string, Conversation>; accessOrder: string[] } => {
  if (accessOrder.length <= MAX_HYDRATED_CONVERSATIONS) {
    return { conversations, accessOrder };
  }

  const evictable = accessOrder.find((id) => {
    if (id === GENERAL_THREAD_ID || id === activeThreadId) return false;
    return !conversations[id]?.disableChat;
  });

  if (!evictable) {
    return { conversations, accessOrder };
  }

  const nextConversations = { ...conversations };
  delete nextConversations[evictable];
  return { conversations: nextConversations, accessOrder: accessOrder.filter((id) => id !== evictable) };
};

const activate = (state: ChatbotState, threadId: string, fallback: Conversation): ChatbotState => {
  const exists = !!state.conversations[threadId];
  const conversations = exists ? state.conversations : { ...state.conversations, [threadId]: fallback };
  const accessOrder = touch(state.accessOrder, threadId);
  const evicted = evict(conversations, accessOrder, threadId);
  return {
    ...state,
    activeThreadId: threadId,
    message: '',
    conversations: evicted.conversations,
    accessOrder: evicted.accessOrder,
  };
};

export const ChatbotReducer = createReducer(
  initialState,
  on(ActivateThread, (state, { threadId }) =>
    activate(state, threadId, createConversation(threadId, threadId === GENERAL_THREAD_ID ? GENERAL_THREAD_TITLE : '')),
  ),
  on(NewChat, (state) => ({
    ...state,
    activeThreadId: GENERAL_THREAD_ID,
    message: '',
    accessOrder: touch(state.accessOrder, GENERAL_THREAD_ID),
    conversations: {
      ...state.conversations,
      [GENERAL_THREAD_ID]: createConversation(GENERAL_THREAD_ID, GENERAL_THREAD_TITLE),
    },
  })),
  on(RemoveConversation, (state, { threadId }) => {
    if (threadId === GENERAL_THREAD_ID || !state.conversations[threadId]) {
      return state;
    }

    const conversations = { ...state.conversations };
    delete conversations[threadId];

    return {
      ...state,
      conversations,
      accessOrder: state.accessOrder.filter((id) => id !== threadId),
      activeThreadId: state.activeThreadId === threadId ? GENERAL_THREAD_ID : state.activeThreadId,
      message: state.activeThreadId === threadId ? '' : state.message,
    };
  }),
  on(ActivateKpiThread, (state, { kpiId, title }) => {
    const threadId = kpiThreadId(kpiId);
    return activate(state, threadId, createConversation(threadId, title));
  }),
  on(AskKpiQuestion, (state, { kpiId, title, message }) => {
    const threadId = kpiThreadId(kpiId);
    const existing = state.conversations[threadId];

    if (existing?.sessionId) {
      return activate(state, threadId, existing);
    }

    const accessOrder = touch(state.accessOrder, threadId);
    const conversations = {
      ...state.conversations,
      [threadId]: {
        ...createConversation(threadId, title),
        showIntro: false,
        disableChat: true,
        messages: [{ sender: Sender.USER, text: message }],
      },
    };
    const evicted = evict(conversations, accessOrder, threadId);
    return {
      ...state,
      activeThreadId: threadId,
      message,
      conversations: evicted.conversations,
      accessOrder: evicted.accessOrder,
    };
  }),
  on(OpenSession, (state, { sessionId }) => {
    const threadId = sessionThreadId(sessionId);
    const existing = state.conversations[threadId];
    const fallback = {
      ...createConversation(threadId, GENERAL_THREAD_TITLE, sessionId),
      showIntro: false,
    };
    return activate(state, threadId, existing ?? fallback);
  }),
  on(LoadSessions, (state) => ({
    ...state,
    loadingSessions: true,
  })),
  on(LoadSessionsSuccess, (state, { sessions }) => ({
    ...state,
    loadingSessions: false,
    pastSessions: sessions,
  })),
  on(LoadSessionsFailed, (state) => ({
    ...state,
    loadingSessions: false,
  })),
  on(LoadUsageSuccess, (state, { usage }) => ({
    ...state,
    usage,
  })),
  on(LoadSessionHistory, (state, { sessionId }) => ({
    ...state,
    conversations: patchConversation(state, sessionThreadId(sessionId), { loadingHistory: true }),
  })),
  on(LoadSessionHistorySuccess, (state, { sessionId, history }) => ({
    ...state,
    conversations: patchConversation(state, sessionThreadId(sessionId), {
      messages: mapHistoryToMessages(history),
      loadingHistory: false,
      showIntro: false,
    }),
  })),
  on(LoadSessionHistoryFailed, (state, { sessionId }) => ({
    ...state,
    conversations: patchConversation(state, sessionThreadId(sessionId), { loadingHistory: false, showError: true }),
  })),
  on(CreateSessionSuccess, (state, { threadId, sessionId }) => ({
    ...state,
    conversations: patchConversation(state, threadId, { sessionId, showError: false }),
  })),
  on(CreateSessionFailed, (state, { threadId }) => ({
    ...state,
    conversations: patchConversation(state, threadId, { showError: true }),
  })),
  on(CreateSessionAndPostQuestionSuccess, (state, { threadId, sessionId }) => ({
    ...state,
    conversations: patchConversation(state, threadId, { sessionId, showError: false }),
  })),
  on(CreateSessionAndPostQuestionFailed, (state, { threadId }) => ({
    ...state,
    conversations: patchConversation(state, threadId, { showError: true }),
  })),
  on(TypeMessage, (state, { message }) => ({
    ...state,
    message,
  })),
  on(PostQuestion, (state, { threadId }) => ({
    ...state,
    conversations: patchConversation(state, threadId, {
      showIntro: false,
      disableChat: true,
      messages: [...ensureConversation(state, threadId).messages, { sender: Sender.USER, text: state.message }],
    }),
  })),
  on(CreateSessionAndPostQuestion, (state, { threadId }) => ({
    ...state,
    conversations: patchConversation(state, threadId, {
      showIntro: false,
      disableChat: true,
      messages: [...ensureConversation(state, threadId).messages, { sender: Sender.USER, text: state.message }],
    }),
  })),
  on(PostQuestionSuccess, (state, { threadId, res }) => {
    const isEnd = res.includes('<END>');
    const cleanChunk = res.replace('<END>', '');
    if (!isEnd) {
      return {
        ...state,
        message: '',
      };
    }
    const conversation = ensureConversation(state, threadId);
    return {
      ...state,
      message: '',
      conversations: patchConversation(state, threadId, {
        disableChat: false,
        messages: [...conversation.messages, { sender: Sender.BOT, text: cleanChunk }],
      }),
    };
  }),
  on(PostQuestionFailed, (state, { threadId }) => ({
    ...state,
    conversations: patchConversation(state, threadId, { disableChat: false }),
  })),
);
